import React, { useMemo, useCallback, useEffect } from "react";
import { MapContainer, TileLayer, ScaleControl, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import FineLayer from "./FineLayer";
import CoarseLayer, { FitBounds, CoarseOutline } from "./CoarseLayer";
import { makeColormap, RAMPS } from "./lib";

const CARTO = "https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png";

function pctile(arr, mask, q) {
  const v = [];
  for (let i = 0; i < arr.length; i++) if (mask[i]) v.push(arr[i]);
  if (!v.length) return 1;
  v.sort((a, b) => a - b);
  return v[Math.min(v.length - 1, Math.floor((q / 100) * v.length))] || 1;
}
const kw = (x) => `${Math.round(x).toLocaleString()}kW`;
const intFmt = (x) => Math.round(x).toLocaleString();
const dec2 = (x) => Number(x).toFixed(2);

function Recenter({ center, zoom, bounds }) {
  const map = useMap();
  useEffect(() => {
    if (!bounds && center) map.setView(center, zoom);
  }, [center, zoom, bounds, map]);
  return null;
}

function Legend({ legend }) {
  if (!legend) return null;
  if (legend.kind === "category") {
    return (
      <div className="cidp-legend" data-testid="map-legend">
        <div className="cidp-legend-title">{legend.title}</div>
        {legend.items.map((it) => (
          <div key={it.label} className="cidp-legend-row">
            <span className="cidp-legend-sw" style={{ background: it.color }} />
            {it.label}
          </div>
        ))}
      </div>
    );
  }
  const grad = `linear-gradient(90deg, ${legend.stops.join(",")})`;
  const f = legend.fmt || intFmt;
  return (
    <div className="cidp-legend" data-testid="map-legend">
      <div className="cidp-legend-title">{legend.title}</div>
      <div className="cidp-legend-bar" style={{ background: grad }} />
      <div className="cidp-legend-scale"><span>{f(legend.min)}</span><span>{f(legend.max)}</span></div>
    </div>
  );
}

export default function MapView({ layer, meta, B, cfg, coarse, grid, fineCounts, bounds, center, zoom, carto }) {
  center = center || (meta ? meta.center : [52.51, 13.405]);
  zoom = zoom || (meta ? meta.zoom : 11);

  const areaKm2 = B ? Math.pow((B.meta.sub * Math.cos((center[0] * Math.PI) / 180)) / 1000, 2) : 1;

  // ---- fine-grid scalars + hover + legend --------------------------------
  const fineProps = useMemo(() => {
    if (!B) return null;
    const ds = cfg.viz_dataset;
    if (layer === "Input Data" && ds === "Population density") {
      const dens = new Float32Array(B.pop.length);
      for (let i = 0; i < dens.length; i++) dens[i] = B.pop[i] / areaKm2;
      const vmax = pctile(dens, B.mask, 98);
      const cm = makeColormap(RAMPS.pop, 0, vmax);
      return {
        colorOf: (s) => cm(dens[s]),
        legend: { kind: "gradient", title: "Population (/km²)", stops: RAMPS.pop, min: 0, max: vmax, fmt: intFmt },
        hover: (s) =>
          B.mask[s]
            ? `<b>Population</b><div>Population: ${B.pop[s].toLocaleString()}</div><div>Density: ${Math.round(dens[s]).toLocaleString()}/km²</div>`
            : null,
      };
    }
    if (layer === "Input Data" && ds === "Demand indicators") {
      const leaf = cfg.viz_ind;
      const k = B.leaves.findIndex((l) => B.labels[B.leaves.indexOf(l)] === leaf);
      const ki = k < 0 ? 0 : k;
      const nl = B.leaves.length;
      const cm = makeColormap(RAMPS.indicator, 0, 1);
      return {
        colorOf: (s) => cm(B.vals[s * nl + ki] / 255),
        legend: { kind: "gradient", title: leaf, stops: RAMPS.indicator, min: 0, max: 1, fmt: dec2 },
        hover: (s) => (B.mask[s] ? groupedHover(B, s) : null),
      };
    }
    if (layer === "Input Data" && ds === "Existing chargers" && cfg.viz_exist_grid === "Fine") {
      const typ = cfg.viz_exist_type;
      const sel = typ === "Slow" ? B.es : typ === "Fast" ? B.ef : B.tot;
      let vmax = 1;
      for (let i = 0; i < sel.length; i++) if (B.mask[i] && sel[i] > vmax) vmax = sel[i];
      const cm = makeColormap(RAMPS.green, 0, vmax);
      return {
        colorOf: (s) => cm(sel[s]),
        legend: { kind: "gradient", title: `Existing chargers (${typ.toLowerCase()})`, stops: RAMPS.green, min: 0, max: vmax, fmt: intFmt },
        hover: (s) =>
          B.mask[s]
            ? `<b>Existing chargers</b><div>Total: ${B.tot[s]}</div><div>Slow: ${B.es[s]}</div><div>Fast: ${B.ef[s]}</div><div>Total power: ${kw(B.ef[s] * cfg.fast_power_kw + B.es[s] * cfg.slow_power_kw)}</div>`
            : null,
      };
    }
    if (layer === "Prioritization Map" && cfg.priorityScore) {
      const sc = cfg.priorityScore;
      const cm = makeColormap(RAMPS.priority, 0, 1);
      return {
        colorOf: (s) => cm(sc[s]),
        legend: { kind: "gradient", title: `Priority score (${cfg.meso_sel.toLowerCase()})`, stops: RAMPS.priority, min: 0, max: 1, fmt: dec2 },
        hover: (s) => (B.mask[s] ? priorityHover(B, s, cfg) : null),
      };
    }
    if (layer === "Fine Distribution" && fineCounts) {
      const fv = cfg.fine_viz;
      if (fv === "Added load") {
        const n = fineCounts.fast.length;
        const load = new Float32Array(n);
        let vmax = 1;
        for (let i = 0; i < n; i++) {
          load[i] = fineCounts.fast[i] * cfg.fast_power_kw + fineCounts.slow[i] * cfg.slow_power_kw;
          if (load[i] > vmax) vmax = load[i];
        }
        const cm = makeColormap(RAMPS.load, 0, vmax);
        return {
          colorOf: (s) => cm(load[s]),
          legend: { kind: "gradient", title: "Added load (kW)", stops: RAMPS.load, min: 0, max: vmax, fmt: kw },
          hover: (s) => (B.mask[s] ? fineHover(B, s, cfg, fineCounts) : null),
        };
      }
      const sel = fv === "Slow chargers" ? fineCounts.slow : fineCounts.fast;
      let vmax = 1;
      for (let i = 0; i < sel.length; i++) if (sel[i] > vmax) vmax = sel[i];
      const cm = makeColormap(RAMPS.green, 0, vmax);
      return {
        colorOf: (s) => cm(sel[s]),
        legend: { kind: "gradient", title: fv, stops: RAMPS.green, min: 0, max: vmax, fmt: intFmt },
        hover: (s) => (B.mask[s] ? fineHover(B, s, cfg, fineCounts) : null),
      };
    }
    return null;
  }, [B, layer, cfg, fineCounts, areaKm2]);

  const colorOf = useCallback((s) => (fineProps ? fineProps.colorOf(s) : "#888"), [fineProps]);
  const hoverHtml = useCallback((s) => (fineProps ? fineProps.hover(s) : null), [fineProps]);

  // ---- coarse choropleth styling/tooltip/legend --------------------------
  const coarseView = useMemo(() => {
    if (!coarse) return null;
    const feats = coarse.features;
    if (layer === "Coarse Distribution") {
      const loads = feats.map((f) => f.properties.added_load_w / 1000);
      const lo = Math.min(...loads), hi = Math.max(...loads) || 1;
      const cm = makeColormap(RAMPS.load, lo, hi);
      return {
        colorOf: (f) => cm(f.properties.added_load_w / 1000),
        legend: { kind: "gradient", title: "Added load (kW)", stops: RAMPS.load, min: lo, max: hi, fmt: kw },
        tooltip: (f) => {
          const p = f.properties;
          const luc = Object.entries(p.land_use_pct).map(([k, v]) => `${k}: ${v.toFixed(0)}%`).join("<br>");
          return `<b>${p.cell}</b><div style="margin:3px 0">${luc}</div><div>Added load: ${kw(p.added_load_w / 1000)}</div><div>Chargers: ${p.added_fast + p.added_slow}</div><div>${p.added_fast} fast / ${p.added_slow} slow</div>`;
        },
      };
    }
    if (layer === "Input Data" && cfg.viz_dataset === "Land use") {
      const colors = meta.landuse.colors;
      const full = meta.landuse.full;
      const list = meta.landuse.list;
      if (cfg.viz_lu === "Dominant") {
        return {
          colorOf: (f) => {
            const luf = f.properties.land_use_pct;
            const dom = list.reduce((a, b) => ((luf[b] || 0) > (luf[a] || 0) ? b : a), list[0]);
            return colors[dom];
          },
          legend: { kind: "category", title: "Dominant land use", items: list.map((k) => ({ label: full[k], color: colors[k] })) },
          tooltip: (f) => luTip(f, list, full),
        };
      }
      const cat = list.find((k) => full[k] === cfg.viz_lu) || list[0];
      const cm = makeColormap(["#ffffff", colors[cat]], 0, 100);
      return {
        colorOf: (f) => cm(f.properties.land_use_pct[cat] || 0),
        legend: { kind: "gradient", title: `${full[cat]} share (%)`, stops: ["#ffffff", colors[cat]], min: 0, max: 100, fmt: (v) => `${Math.round(v)}%` },
        tooltip: (f) => luTip(f, list, full),
      };
    }
    if (layer === "Input Data" && cfg.viz_dataset === "Existing chargers" && cfg.viz_exist_grid === "Coarse") {
      const typ = cfg.viz_exist_type;
      const val = (f) => {
        const p = f.properties;
        return typ === "Slow" ? p.existing_slow : typ === "Fast" ? p.existing_fast : p.existing_fast + p.existing_slow;
      };
      const vmax = Math.max(1, ...feats.map(val));
      const cm = makeColormap(RAMPS.green, 0, vmax);
      return {
        colorOf: (f) => cm(val(f)),
        legend: { kind: "gradient", title: `Existing chargers (${typ.toLowerCase()})`, stops: RAMPS.green, min: 0, max: vmax, fmt: intFmt },
        tooltip: (f) => {
          const p = f.properties;
          return `<b>Existing chargers</b><div>Total: ${p.existing_fast + p.existing_slow}</div><div>Slow: ${p.existing_slow}</div><div>Fast: ${p.existing_fast}</div><div>Total power: ${kw(p.existing_fast * cfg.fast_power_kw + p.existing_slow * cfg.slow_power_kw)}</div>`;
        },
      };
    }
    return null;
  }, [coarse, layer, cfg, meta]);

  const gridView = useMemo(() => {
    if (layer !== "Electrical Grid Validation" || !grid || !grid.geojson) return null;
    const skipped = grid.status === "skipped" || grid.status === "disabled";
    const feats = grid.geojson.features;
    const exc = feats.filter((f) => f.properties.feasible === false).map((f) => f.properties.excess_load_w / 1000);
    const cm = makeColormap(RAMPS.excess, exc.length ? Math.min(...exc) : 0, exc.length ? Math.max(...exc) : 1);
    const items = skipped
      ? [{ label: "Added load (no feasibility data)", color: "#b9c6d6" }]
      : [
          { label: "Feasible", color: "#2ca25f" },
          { label: "Low excess load", color: RAMPS.excess[0] },
          { label: "High excess load", color: RAMPS.excess[2] },
          { label: "No data", color: "#b9c6d6" },
        ];
    return {
      colorOf: (f) => {
        const p = f.properties;
        if (skipped || p.feasible === null) return "#b9c6d6";
        return p.feasible ? "#2ca25f" : cm(p.excess_load_w / 1000);
      },
      legend: { kind: "category", title: skipped ? "Added load" : "Grid feasibility", items },
      tooltip: (f) => {
        const p = f.properties;
        const feas = p.feasible === null ? "-" : p.feasible ? "Yes" : "No";
        const ex = p.excess_load_w === null ? "-" : kw(p.excess_load_w / 1000);
        return `<b>Node ${p.node_id}</b><div>Feasible: ${feas}</div><div>Added load: ${kw(p.added_load_w / 1000)}</div><div>Excess load: ${ex}</div>`;
      },
    };
  }, [grid, layer]);

  const active = coarseView || gridView;
  const legend = (fineProps && fineProps.legend) || (active && active.legend) || null;
  const showOutline = !!fineProps && !!coarse;

  return (
    <>
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          url={carto ? `${CARTO}?key=${carto}` : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"}
          attribution="© OpenStreetMap © CARTO"
        />
        <ScaleControl position="bottomleft" imperial={false} />
        {bounds && <FitBounds bounds={bounds} />}
        <Recenter center={center} zoom={zoom} bounds={bounds} />
        {fineProps && B && (
          <FineLayer bundle={B} colorOf={colorOf} hoverHtml={hoverHtml} visibleOf={fineProps.visibleOf} />
        )}
        {showOutline && <CoarseOutline fc={coarse} styleKey={cfg.params_hash} />}
        {active && coarse && (
          <CoarseLayer
            fc={layer === "Electrical Grid Validation" ? grid.geojson : coarse}
            styleKey={`${layer}-${cfg.viz_dataset}-${cfg.viz_lu}-${cfg.viz_exist_type}-${cfg.viz_exist_grid}-${JSON.stringify(cfg.params_hash)}`}
            colorOf={active.colorOf}
            tooltipHtml={active.tooltip}
          />
        )}
      </MapContainer>
      {legend && <Legend legend={legend} />}
    </>
  );
}

function luTip(f, list, full) {
  const lu = f.properties.land_use_pct;
  const dom = list.reduce((a, b) => ((lu[b] || 0) > (lu[a] || 0) ? b : a), list[0]);
  const rows = list.map((k) => `${full[k]}: ${(lu[k] || 0).toFixed(0)}%`).join("<br>");
  return `<b>Dominant: ${full[dom]}</b><div style="margin-top:3px">${rows}</div>`;
}
function groupedHover(B, s) {
  const nl = B.leaves.length;
  let h = '<b>Demand indicators</b><table style="border-collapse:collapse">';
  for (const [g, idxs] of B.groups) {
    h += `<tr><td colspan="2" style="color:#008eff;padding-top:3px">${g}</td></tr>`;
    for (const k of idxs) h += `<tr><td style="padding-right:8px">${B.labels[k]}</td><td style="text-align:right">${(B.vals[s * nl + k] / 255).toFixed(3)}</td></tr>`;
  }
  return h + "</table>";
}
function priorityHover(B, s, cfg) {
  const nl = B.leaves.length;
  let totF = 0;
  let totS = 0;
  let h = '<b>Priority indicators</b><table style="border-collapse:collapse"><tr><td style="color:#5b6b80">Indicator</td><td style="text-align:right;color:#5b6b80">Score</td></tr>';
  for (const [g, idxs] of B.groups) {
    h += `<tr><td colspan="2" style="color:#008eff;padding-top:3px">${g}</td></tr>`;
    for (const k of idxs) {
      const ind = B.vals[s * nl + k] / 255;
      totF += ind * (cfg.meso_fast[B.leaves[k]] || 0);
      totS += ind * (cfg.meso_slow[B.leaves[k]] || 0);
      h += `<tr><td style="padding-right:8px">${B.labels[k]}</td><td style="text-align:right">${ind.toFixed(3)}</td></tr>`;
    }
  }
  h += `<tr><td style="border-top:1px solid #d8e2ee">Priority score (fast)</td><td style="border-top:1px solid #d8e2ee;text-align:right">${totF.toFixed(3)}</td></tr>`;
  h += `<tr><td>Priority score (slow)</td><td style="text-align:right">${totS.toFixed(3)}</td></tr>`;
  return h + "</table>";
}
function fineHover(B, s, cfg, fc) {
  const nf = fc.fast[s];
  const ns = fc.slow[s];
  const load = nf * cfg.fast_power_kw + ns * cfg.slow_power_kw;
  return `<b>Sub-cell</b><div>Fast assigned: ${nf}</div><div>Slow assigned: ${ns}</div><div>Added load: ${kw(load)}</div>`;
}
